import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Sparkles, Mail, ArrowLeft } from 'lucide-react';
import { getFirebaseAuth, isFirebaseClientConfigured } from '../lib/firebaseClient';
import { formatFirebaseAuthError } from '../lib/firebaseAuthErrors';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import LocaleSelector from '../components/LocaleSelector';

const ForgotPassword = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isFirebaseClientConfigured()) {
      setError(copy.auth.firebaseNotConfigured);
      return;
    }
    setLoading(true);

    try {
      const auth = getFirebaseAuth();
      const continueUrl = typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined;
      await sendPasswordResetEmail(auth, email, continueUrl ? { url: continueUrl, handleCodeInApp: false } : undefined);
      setSuccess(true);
    } catch (err) {
      setError(formatFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/login" className="inline-flex items-center text-[#B6B3C9] hover:text-[#F5F4FF] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {copy.auth.backToLogin}
            </Link>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-br from-[#7C5CFF] to-[#A88BFF] p-3 rounded-2xl">
                <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-4xl font-light tracking-tight text-[#F5F4FF] mb-2">{copy.auth.resetPassword}</h1>
            <p className="text-[#B6B3C9]">{copy.auth.resetSubtitle}</p>
          </div>

          <div className="bg-[#141417] border border-[#2B2B35]/50 rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
            {success ? (
              <div data-testid="reset-success-message" className="text-center">
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-sm mb-4">
                  {copy.auth.resetSuccess}
                </div>
                <p className="text-[#B6B3C9] text-sm mb-4">
                  {copy.auth.resetCheckEmail}
                </p>
                <Link
                  to="/login"
                  className="inline-block bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-3 transition-all font-medium"
                >
                  {copy.auth.backToLogin}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div data-testid="reset-error-message" className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm mb-4">
                    {error}
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.auth.emailAddress}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8F8AA8] w-5 h-5" strokeWidth={1.5} />
                    <input
                      data-testid="forgot-password-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <button
                  data-testid="forgot-password-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-4 transition-all duration-300 shadow-[0_4px_14px_rgba(124,92,255,0.32)] hover:shadow-[0_6px_20px_rgba(124,92,255,0.45)] font-medium tracking-wide disabled:opacity-50"
                >
                  {loading ? copy.auth.sending : copy.auth.sendResetLink}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-cover bg-center relative" style={{
        backgroundImage: `url('${copy.media.authHero}')`
      }}>
        <div className="absolute right-6 top-6 z-20">
          <LocaleSelector />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#050507]/75 to-[#050507]/40"></div>
      </div>
    </div>
  );
};

export default ForgotPassword;




